import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { Loader2, Mail, RefreshCw, LogOut, ShieldCheck } from 'lucide-react';

interface EmailVerificationGateProps {
  onVerified: () => void;
}

const EmailVerificationGate: React.FC<EmailVerificationGateProps> = ({ onVerified }) => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string>('');
  const [isVerified, setIsVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const canResend = useMemo(() => !!email && email.includes('@') && !resendLoading, [email, resendLoading]);

  const loadSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const user = session?.user;
      if (!user) {
        setEmail('');
        setIsVerified(false);
        setLoading(false);
        return;
      }

      setEmail(user.email || '');
      const verified = !!user.email_confirmed_at;
      setIsVerified(verified);

      if (verified) onVerified();
    } catch (e: any) {
      setError(e?.message || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadSession();
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    setResendLoading(true);
    setError(null);
    setInfo(null);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (resendError) throw resendError;
      setInfo('Verification email sent. Please check your inbox.');
    } catch (e: any) {
      setError(e?.message || 'Failed to resend email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (isVerified) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-slate-900/60 border border-slate-800 rounded-3xl shadow-2xl p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Mail className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-black text-white" data-testid="email-verification-title">Verify your email</h1>
            <p className="text-sm text-slate-400 mt-2" data-testid="email-verification-description">
              We sent a confirmation link to <span className="text-slate-200 font-medium">{email || 'your email'}</span>.
              Open it to activate your account.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 text-sm p-3 rounded-xl" data-testid="email-verification-error">
              {error}
            </div>
          )}
          {info && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 text-sm p-3 rounded-xl" data-testid="email-verification-info">
              {info}
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3">
          <button
            onClick={loadSession}
            className="w-full bg-slate-950 hover:bg-slate-800 border border-slate-700 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
            data-testid="email-verification-refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
            I’ve verified — refresh
          </button>

          <button
            onClick={handleResend}
            disabled={!canResend}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
            data-testid="email-verification-resend-button"
          >
            {resendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Resend verification email
          </button>

          <button
            onClick={handleLogout}
            className="w-full text-slate-400 hover:text-white py-2 rounded-xl font-medium flex items-center justify-center gap-2"
            data-testid="email-verification-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        <p className="text-xs text-slate-500 mt-6" data-testid="email-verification-hint">
          Tip: Check spam/junk folders. If you changed Auth settings recently, resend the email.
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationGate;
